import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useI18n } from "../lib/i18n";
import Header from "../components/Header";
import Logo from "../components/Logo";
import PasswordChecklist, { passwordIsValid } from "../components/PasswordChecklist";
import { ArrowRight, CheckCircle } from "lucide-react";

export function PasswordResetRequest() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/password-reset/request", { email });
      setSent(true);
    } catch {}
    finally { setLoading(false); }
  };

  return (
    <div data-testid="pwd-request-page" className="min-h-screen text-[#F5F0EB]">
      <Header />
      <div className="min-h-screen flex items-center justify-center px-6 pt-24 pb-16">
        <div className="w-full max-w-md">
          <div className="flex justify-center mb-8"><Logo size={64} variant="mark" /></div>
          {sent ? (
            <div data-testid="pwd-request-sent" className="text-center">
              <CheckCircle className="mx-auto text-[#C5A059]" size={40} />
              <h1 className="font-serif text-3xl font-light mt-6">Vérifiez votre courriel</h1>
              <p className="text-[#A8A39D] text-sm mt-4">Si un compte existe pour <strong>{email}</strong>, un lien de réinitialisation y a été envoyé.</p>
              <Link to="/login" className="btn-ghost mt-8 inline-flex">Retour</Link>
            </div>
          ) : (
            <>
              <div className="overline mb-6 text-center">Réinitialiser le mot de passe</div>
              <h1 className="font-serif text-4xl font-light mb-10 text-center">Récupération</h1>
              <form onSubmit={submit} className="space-y-6">
                <div>
                  <label className="overline text-xs">Courriel</label>
                  <input data-testid="pwd-req-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="luxe-input mt-2" />
                </div>
                <button data-testid="pwd-req-submit" type="submit" disabled={loading} className="btn-gold w-full mt-4">
                  {loading ? "..." : <>Envoyer le lien <ArrowRight size={14} /></>}
                </button>
              </form>
              <div className="text-center mt-8 text-xs text-[#A8A39D]">
                <Link to="/login" className="hover:text-[#F5F0EB]">Retour à la connexion</Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export function PasswordResetConfirm() {
  const { token } = useParams();
  const { lang } = useI18n();
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const pwdValid = passwordIsValid(pw);
  const pwdMatch = confirm.length > 0 && confirm === pw;
  const ready = pwdValid && pwdMatch;

  const submit = async (e) => {
    e.preventDefault();
    if (!ready) return;
    setLoading(true); setErr("");
    try {
      await api.post("/auth/password-reset/confirm", { token, new_password: pw });
      setDone(true);
      setTimeout(() => nav("/login"), 2200);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="pwd-confirm-page" className="min-h-screen text-[#F5F0EB]">
      <Header />
      <div className="min-h-screen flex items-center justify-center px-6 pt-24 pb-16">
        <div className="w-full max-w-md">
          <div className="flex justify-center mb-8"><Logo size={64} variant="mark" /></div>
          {done ? (
            <div className="text-center">
              <CheckCircle className="mx-auto text-[#C5A059]" size={40} />
              <h1 className="font-serif text-3xl font-light mt-6">Mot de passe mis à jour</h1>
              <p className="text-[#A8A39D] text-sm mt-4">Redirection vers la connexion...</p>
            </div>
          ) : (
            <>
              <div className="overline mb-6 text-center">Nouveau mot de passe</div>
              <h1 className="font-serif text-4xl font-light mb-10 text-center">Définir</h1>
              <form onSubmit={submit} className="space-y-6">
                <div>
                  <label className="overline text-xs">Nouveau mot de passe</label>
                  <input data-testid="pwd-conf-new" type="password" required value={pw} onChange={(e) => setPw(e.target.value)} className="luxe-input mt-2" autoComplete="new-password" />
                </div>
                <div>
                  <label className="overline text-xs">Confirmer le mot de passe</label>
                  <input data-testid="pwd-conf-confirm" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} className="luxe-input mt-2" autoComplete="new-password" />
                  <PasswordChecklist password={pw} confirm={confirm} showConfirmRow lang={lang} />
                </div>
                {err && <div data-testid="pwd-conf-error" className="text-red-400 text-sm">{err}</div>}
                <button data-testid="pwd-conf-submit" type="submit" disabled={loading || !ready} className="btn-gold w-full mt-4 disabled:opacity-40 disabled:cursor-not-allowed">
                  {loading ? "..." : <>Valider <ArrowRight size={14} /></>}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
